/**
 * Irrigation Season API Service
 *
 * This service provides methods for interacting with irrigation season
 * backend endpoints including CRUD operations, water allocation tracking,
 * and farm-level season management.
 */

import apiClient from './apiClient';
import type {
  IrrigationSeason,
  AllocationStatus,
  WaterAllocationTracking,
  SeasonSummary,
  CreateSeasonRequest,
  UpdateSeasonRequest,
  CloseSeasonRequest,
  RecordWaterUsageRequest,
  CheckAllocationRequest,
  CheckAllocationResponse,
  ListSeasonsParams,
  ListSeasonsResponse,
  ListTrackingParams,
  ListTrackingResponse,
  ActiveSeasonResponse,
} from '@/types/irrigationSeason';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Build query string from parameters
 */
const buildQueryString = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

// ============================================================================
// Core CRUD Methods
// ============================================================================

/**
 * Create a new irrigation season for a farm.
 *
 * @param farmId - The farm ID (UUID)
 * @param data - Season creation data
 * @returns The created irrigation season
 */
export const createSeason = async (
  farmId: string,
  data: CreateSeasonRequest
): Promise<IrrigationSeason> => {
  return apiClient.post<IrrigationSeason>(
    `/irrigation-seasons/farms/${farmId}/seasons`,
    data
  );
};

/**
 * List irrigation seasons for a farm with pagination and filtering.
 *
 * @param farmId - The farm ID (UUID)
 * @param params - Optional query parameters for filtering and pagination
 * @returns Paginated list of irrigation seasons
 */
export const getSeasons = async (
  farmId: string,
  params?: ListSeasonsParams
): Promise<ListSeasonsResponse> => {
  const queryString = buildQueryString(params || {});
  return apiClient.get<ListSeasonsResponse>(
    `/irrigation-seasons/farms/${farmId}/seasons${queryString}`
  );
};

/**
 * Get detailed information about a specific irrigation season.
 *
 * @param seasonId - The season ID
 * @returns The irrigation season details
 */
export const getSeason = async (seasonId: number): Promise<IrrigationSeason> => {
  return apiClient.get<IrrigationSeason>(`/irrigation-seasons/seasons/${seasonId}`);
};

/**
 * Update an existing irrigation season.
 *
 * @param seasonId - The season ID
 * @param data - Season update data (partial)
 * @returns The updated irrigation season
 */
export const updateSeason = async (
  seasonId: number,
  data: UpdateSeasonRequest
): Promise<IrrigationSeason> => {
  return apiClient.put<IrrigationSeason>(
    `/irrigation-seasons/seasons/${seasonId}`,
    data
  );
};

/**
 * Close an irrigation season.
 *
 * @param seasonId - The season ID
 * @param endDate - Optional end date (ISO date string), defaults to today if not provided
 * @returns The closed irrigation season
 */
export const closeSeason = async (
  seasonId: number,
  endDate?: string
): Promise<IrrigationSeason> => {
  const queryString = endDate ? `?end_date=${encodeURIComponent(endDate)}` : '';
  return apiClient.post<IrrigationSeason>(
    `/irrigation-seasons/seasons/${seasonId}/close${queryString}`,
    {}
  );
};

/**
 * Permanently delete an irrigation season and all its related data.
 *
 * @param seasonId - The season ID
 */
export const deleteSeason = async (seasonId: number): Promise<void> => {
  await apiClient.delete<void>(`/irrigation-seasons/seasons/${seasonId}`);
};

// ============================================================================
// Water Allocation Methods
// ============================================================================

/**
 * Get current allocation status for a season with projections.
 *
 * @param seasonId - The season ID
 * @returns Current allocation status including usage, remaining, projections, and risk assessment
 */
export const getAllocationStatus = async (
  seasonId: number
): Promise<AllocationStatus> => {
  return apiClient.get<AllocationStatus>(
    `/irrigation-seasons/seasons/${seasonId}/allocation`
  );
};

/**
 * Get comprehensive season summary including status, allocation, and tracking.
 *
 * @param seasonId - The season ID
 * @param speciesId - Optional species ID to filter stage performance data
 * @returns Complete season summary with allocation status and tracking summary
 */
export const getSeasonSummary = async (
  seasonId: number,
  speciesId?: number
): Promise<SeasonSummary> => {
  const queryString = buildQueryString({ species_id: speciesId });
  return apiClient.get<SeasonSummary>(
    `/irrigation-seasons/seasons/${seasonId}/summary${queryString}`
  );
};

/**
 * Get water tracking records for a season with pagination.
 *
 * @param seasonId - The season ID
 * @param params - Optional query parameters for filtering and pagination
 * @returns Paginated list of water tracking records
 */
export const getWaterTracking = async (
  seasonId: number,
  params?: ListTrackingParams
): Promise<ListTrackingResponse> => {
  const queryString = buildQueryString(params || {});
  return apiClient.get<ListTrackingResponse>(
    `/irrigation-seasons/seasons/${seasonId}/tracking${queryString}`
  );
};

/**
 * Record daily water usage for a season.
 *
 * @param seasonId - The season ID
 * @param data - Water usage recording data
 * @returns The created water tracking record
 */
export const recordWaterUsage = async (
  seasonId: number,
  data: RecordWaterUsageRequest
): Promise<WaterAllocationTracking> => {
  return apiClient.post<WaterAllocationTracking>(
    `/irrigation-seasons/seasons/${seasonId}/tracking`,
    data
  );
};

/**
 * Check allocation limit before recording water usage.
 *
 * @param seasonId - The season ID
 * @param additionalM3 - Additional water usage in cubic meters to check
 * @returns Allocation check result with before/after comparison and warning if needed
 */
export const checkAllocation = async (
  seasonId: number,
  additionalM3: number
): Promise<CheckAllocationResponse> => {
  const requestBody: CheckAllocationRequest = {
    proposed_irrigation_m3: additionalM3,
  };
  return apiClient.post<CheckAllocationResponse>(
    `/irrigation-seasons/seasons/${seasonId}/check-allocation`,
    requestBody
  );
};

// ============================================================================
// Farm-Level Methods
// ============================================================================

/**
 * Get the active irrigation season for a farm.
 *
 * @param farmId - The farm ID (UUID)
 * @param speciesId - Optional species ID to filter by
 * @returns Active season response with season and allocation status
 */
export const getActiveSeason = async (
  farmId: string,
  speciesId?: number
): Promise<ActiveSeasonResponse> => {
  const queryString = buildQueryString({ species_id: speciesId });
  return apiClient.get<ActiveSeasonResponse>(
    `/irrigation-seasons/farms/${farmId}/active-season${queryString}`
  );
};

/**
 * Download the backend-rendered PDF season report and trigger a browser save.
 * Uses an authenticated fetch (Bearer token) — the file can't be served
 * with a plain <a href> because the endpoint requires auth headers.
 */
export const downloadSeasonReport = async (
  seasonId: number,
  opts: { lang?: 'es' | 'en'; eventsWindowDays?: number; filename?: string } = {}
): Promise<void> => {
  const params = new URLSearchParams();
  if (opts.lang) params.set('lang', opts.lang);
  if (opts.eventsWindowDays != null) {
    params.set('events_window_days', String(opts.eventsWindowDays));
  }
  const qs = params.toString() ? `?${params.toString()}` : '';
  const blob = await apiClient.getBlob(
    `/irrigation-seasons/seasons/${seasonId}/report.pdf${qs}`,
  );
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = opts.filename || `season-report-${seasonId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
};

/** Enqueue an emailed copy of the season report to the current user. */
export const emailSeasonReport = async (
  seasonId: number,
  opts: { lang?: 'es' | 'en'; eventsWindowDays?: number } = {}
): Promise<{ task_id: string; to: string }> => {
  const params = new URLSearchParams();
  if (opts.lang) params.set('lang', opts.lang);
  if (opts.eventsWindowDays != null) {
    params.set('events_window_days', String(opts.eventsWindowDays));
  }
  const qs = params.toString() ? `?${params.toString()}` : '';
  return apiClient.post<{ task_id: string; to: string }>(
    `/irrigation-seasons/seasons/${seasonId}/report/email${qs}`,
    {},
  );
};

// ============================================================================
// Export all API functions
// ============================================================================

const irrigationSeasonApi = {
  // Core CRUD Methods
  createSeason,
  getSeasons,
  getSeason,
  updateSeason,
  closeSeason,
  deleteSeason,
  // Water Allocation Methods
  getAllocationStatus,
  getSeasonSummary,
  getWaterTracking,
  recordWaterUsage,
  checkAllocation,
  // Farm-Level Methods
  getActiveSeason,
  // Reports
  downloadSeasonReport,
  emailSeasonReport,
};

export default irrigationSeasonApi;
